import { useState, useEffect } from "react";
import { Alert } from "@mui/material";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import {
  CreditCard,
  Zap,
  CheckCircle,
  Clock,
  AlertCircle,
  Users,
  CalendarPlus,
} from "lucide-react";

const ConvertTrialCard = ({
  accountData,
  subscriptionPlans,
  onConvert,
  onExtendTrial,
  loading,
  extendingTrial,
  message,
  extendMessage,
}) => {
  // Get actual user count, default to 1 if not available
  const actualUserCount = accountData.actualUserCount || 1;

  const [formData, setFormData] = useState({
    subscriptionPlan: "",
    billingCycle: "monthly",
    userLicenseCount: actualUserCount, // Default to actual user count
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extensionDays, setExtensionDays] = useState(14);

  // Calculate days remaining in trial
  const getDaysRemaining = () => {
    if (!accountData.trialEndDate) return null;
    const today = new Date();
    const trialEnd = new Date(accountData.trialEndDate);
    const diffTime = trialEnd - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = getDaysRemaining();

  // Update selected plan when form data changes
  useEffect(() => {
    if (formData.subscriptionPlan && subscriptionPlans.length > 0) {
      const plan = subscriptionPlans.find(
        (p) => p.name === formData.subscriptionPlan
      );
      setSelectedPlan(plan);
    } else {
      setSelectedPlan(null);
    }
  }, [formData.subscriptionPlan, subscriptionPlans]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Calculate total cost
  const calculateTotal = () => {
    if (!selectedPlan) return 0;
    const basePrice = selectedPlan.price;
    const multiplier = formData.billingCycle === "yearly" ? 12 : 1;
    const discount = formData.billingCycle === "yearly" ? 0.8 : 1; // 20% yearly discount
    return (basePrice * multiplier * discount).toFixed(2);
  };

  const handleConvertClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmConvert = () => {
    setShowConfirmation(false);
    onConvert(formData);
  };

  const handleExtendClick = () => {
    setShowExtendModal(true);
  };

  const handleConfirmExtend = () => {
    setShowExtendModal(false);
    onExtendTrial(extensionDays);
  };

  const isFormValid = () => {
    return (
      formData.subscriptionPlan &&
      formData.billingCycle &&
      formData.userLicenseCount >= actualUserCount
    );
  };

  return (
    <div className="space-y-6">
      {/* Trial Status Banner */}
      <Card className="border-0 shadow-lg shadow-orange-200/50 bg-gradient-to-br from-orange-50 to-amber-50 border-l-4 border-l-orange-500">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Trial Account - Unlimited Users
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                {daysRemaining !== null ? (
                  daysRemaining > 0 ? (
                    <>
                      This client's trial expires in{" "}
                      <span className="font-bold text-orange-600">
                        {daysRemaining} day{daysRemaining !== 1 ? "s" : ""}
                      </span>
                      . They currently have unlimited user licenses during their
                      trial. When converting to paid, select at least{" "}
                      <span className="font-semibold">
                        {actualUserCount} license
                        {actualUserCount !== 1 ? "s" : ""}
                      </span>{" "}
                      to cover their existing users.
                    </>
                  ) : (
                    <span className="font-bold text-red-600">
                      This client's trial has expired. Convert to a paid account
                      to restore their access.
                    </span>
                  )
                ) : (
                  <>
                    This client currently has unlimited user licenses during
                    their trial. Convert to a paid account to continue service.
                  </>
                )}
              </p>
              {accountData.trialEndDate && (
                <p className="text-xs text-gray-600">
                  Trial End Date:{" "}
                  {new Date(accountData.trialEndDate).toLocaleDateString()}
                </p>
              )}
              <div className="mt-4">
                <Button
                  onClick={handleExtendClick}
                  disabled={extendingTrial}
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400"
                >
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  {extendingTrial
                    ? "Extending Trial..."
                    : "Extend Trial Period"}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Extend Trial Message */}
      {extendMessage && (
        <Alert
          severity={extendMessage.type === "success" ? "success" : "error"}
          className="mb-4"
        >
          {extendMessage.text}
        </Alert>
      )}

      {/* Extend Trial Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarPlus className="h-5 w-5 text-orange-500" />
                Extend Trial Period
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-700">
                Extend this client's trial period to give them more time to
                evaluate the product.
              </p>
              <div className="space-y-2">
                <Label
                  htmlFor="extensionDays"
                  className="text-sm font-medium text-gray-700"
                >
                  Extension Days
                </Label>
                <Select
                  value={extensionDays.toString()}
                  onValueChange={(value) => setExtensionDays(parseInt(value))}
                >
                  <SelectTrigger className="w-full bg-white border-gray-300">
                    <SelectValue>
                      {extensionDays === 7 && "7 days"}
                      {extensionDays === 14 && "14 days (recommended)"}
                      {extensionDays === 30 && "30 days"}
                      {extensionDays === 60 && "60 days"}
                      {extensionDays === 90 && "90 days"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days (recommended)</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {accountData.trialEndDate && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    <span className="font-medium">Current End Date:</span>{" "}
                    {new Date(accountData.trialEndDate).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-blue-900 mt-1">
                    <span className="font-medium">New End Date:</span>{" "}
                    {new Date(
                      new Date(accountData.trialEndDate).getTime() +
                        extensionDays * 24 * 60 * 60 * 1000
                    ).toLocaleDateString()}
                  </p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowExtendModal(false)}
                  className="flex-1"
                  disabled={extendingTrial}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmExtend}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={extendingTrial}
                >
                  {extendingTrial ? "Extending..." : "Confirm Extension"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Conversion Form */}
      <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
        <CardHeader className="pb-4 lg:pb-6">
          <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25">
              <Zap className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
            </div>
            <span className="text-base lg:text-xl">
              Convert Client to Paid Account
            </span>
          </CardTitle>
          <CardDescription>
            Select a plan for your client and set up their paid subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Message Alert */}
          {message && (
            <Alert
              severity={message.type === "success" ? "success" : "error"}
              className="mb-4"
            >
              {message.text}
            </Alert>
          )}

          {/* Confirmation Modal */}
          {showConfirmation && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="max-w-md w-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                    Confirm Conversion
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-700">
                    Convert this client's trial account to a paid subscription
                    with the following details:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Plan:</span>
                      <span className="font-semibold">
                        {formData.subscriptionPlan}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Billing:</span>
                      <span className="font-semibold capitalize">
                        {formData.billingCycle}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">User Licenses:</span>
                      <span className="font-semibold">
                        {formData.userLicenseCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                      <span>Client's Cost:</span>
                      <span className="text-green-600">
                        ${calculateTotal()}/
                        {formData.billingCycle === "yearly" ? "year" : "month"}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowConfirmation(false)}
                      className="flex-1"
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmConvert}
                      className="flex-1 bg-[#00C68D] hover:bg-[#01C891] text-white"
                      disabled={loading}
                    >
                      {loading ? "Converting..." : "Confirm & Convert"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Plan Selection */}
          <div>
            <Label
              htmlFor="subscriptionPlan"
              className="text-sm font-medium text-gray-700 mb-2 block"
            >
              Select Client's Plan *
            </Label>
            <Select
              value={formData.subscriptionPlan}
              onValueChange={(value) => handleChange("subscriptionPlan", value)}
            >
              <SelectTrigger className="w-full bg-white border-gray-300">
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {subscriptionPlans.map((plan) => (
                  <SelectItem key={plan._id} value={plan.name}>
                    <div className="flex items-center justify-between gap-4 w-full">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{plan.name}</span>
                        {plan.isPopular && (
                          <Badge className="bg-orange-500 text-white text-xs px-2 py-0.5">
                            Popular
                          </Badge>
                        )}
                      </div>
                      <span className="text-gray-500 text-sm">
                        ${plan.price}/{plan.billingCycle}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Plan Details */}
          {selectedPlan && (
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {selectedPlan.name}
                    </h4>
                    {selectedPlan.description && (
                      <p className="text-sm text-gray-700 mb-3">
                        {selectedPlan.description}
                      </p>
                    )}
                    {selectedPlan.features &&
                      selectedPlan.features.length > 0 && (
                        <ul className="space-y-1">
                          {selectedPlan.features.map((feature, index) => (
                            <li
                              key={index}
                              className="text-sm text-gray-700 flex items-start gap-2"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Billing Cycle and User Count */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="billingCycle"
                className="text-sm font-medium text-gray-700 mb-2 block"
              >
                Billing Cycle *
              </Label>
              <Select
                value={formData.billingCycle}
                onValueChange={(value) => handleChange("billingCycle", value)}
              >
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue>
                    {formData.billingCycle === "monthly"
                      ? "Monthly"
                      : "Yearly (Save 20%)"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">
                    <div className="flex items-center gap-2">
                      <span>Yearly</span>
                      <Badge className="bg-green-500 text-white text-xs px-2 py-0.5">
                        Save 20%
                      </Badge>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="userLicenseCount"
                className="text-sm font-medium text-gray-700 mb-2 block"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Number of User Licenses *
                </div>
              </Label>
              <Input
                id="userLicenseCount"
                type="number"
                min={actualUserCount}
                value={formData.userLicenseCount}
                onChange={(e) =>
                  handleChange(
                    "userLicenseCount",
                    parseInt(e.target.value) || actualUserCount
                  )
                }
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Currently {actualUserCount} user
                {actualUserCount !== 1 ? "s" : ""} in account. Minimum licenses
                required: {actualUserCount}
              </p>
            </div>
          </div>

          {/* Pricing Summary */}
          {selectedPlan && (
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Base Price:</span>
                    <span className="font-medium">
                      ${selectedPlan.price}/{selectedPlan.billingCycle}
                    </span>
                  </div>
                  {formData.billingCycle === "yearly" && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Yearly Discount:</span>
                        <span className="font-medium text-green-600">-20%</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>Client Will Pay:</span>
                    <span className="text-green-600">
                      ${calculateTotal()}/
                      {formData.billingCycle === "yearly" ? "year" : "month"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Convert Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleConvertClick}
              disabled={!isFormValid() || loading}
              className="bg-[#00C68D] hover:bg-[#01C891] text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              {loading
                ? "Converting Client Account..."
                : "Convert Client to Paid"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConvertTrialCard;
